import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { X, Check, ZoomIn } from 'lucide-react';

interface ImageCropModalProps {
    isOpen: boolean;
    imageSrc: string;
    aspect: number;
    aspectLabel?: string;
    isSubmitting?: boolean;
    onCancel: () => void;
    onConfirm: (croppedAreaPixels: Area) => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
    isOpen,
    imageSrc,
    aspect,
    aspectLabel,
    isSubmitting = false,
    onCancel,
    onConfirm,
}) => {
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    useEffect(() => {
        if (isOpen) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
        }
    }, [isOpen, imageSrc]);

    const handleCropComplete = useCallback((_croppedArea: Area, areaPixels: Area) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-4 max-w-2xl">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-dark dark:text-light">Görseli Kırp</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600"
                        type="button"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4">
                    {aspectLabel && (
                        <p className="text-sm text-gray-500 mb-3">
                            Bu görsel <span className="font-medium">{aspectLabel}</span> oranında gösterilecek. Kaydırıp
                            yakınlaştırarak kırpma alanını ayarlayın.
                        </p>
                    )}
                    <div className="relative w-full h-[420px] bg-gray-900 rounded-lg overflow-hidden">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                        />
                    </div>
                    <div className="flex items-center gap-3 mt-4">
                        <ZoomIn size={18} className="text-gray-400" />
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-2 justify-end">
                    <button onClick={onCancel} className="btn btn-secondary" type="button" disabled={isSubmitting}>
                        Vazgeç
                    </button>
                    <button
                        onClick={() => croppedAreaPixels && onConfirm(croppedAreaPixels)}
                        disabled={!croppedAreaPixels || isSubmitting}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition"
                        type="button"
                    >
                        <Check size={16} />
                        {isSubmitting ? 'Yükleniyor...' : 'Kırp ve Kullan'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
